<template>
  <modal size="lg" content-class="bg-gray-200" @close="$emit('close')">
    <template #header>
      <div class="w-100 d-flex align-items-center" style="gap: 8px">
        <span v-if="data != null" class="font-weight-bold">
          Account #{{ data.user.member_number }}
          <span class="text-muted font-weight-normal ml-1">({{ data.total_nominations }})</span>
        </span>
        <span v-else class="font-weight-bold">Account detail</span>
        <router-link v-if="profileRoute != null" :to="profileRoute" target="_blank" class="icon-btn icon-btn--view" title="View profile" />
      </div>
    </template>

    <template #body>
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status" />
      </div>

      <div v-else-if="data != null" class="partial-payment-popup">
        <form-alert :message="alert.message.value" :type="alert.type.value" class="mb-3" />

        <div v-for="member in data.members" :key="member.id" class="mb-3">
          <div class="partial-payment-popup__member-name mb-1">
            <strong>{{ member.full_name }}</strong>
          </div>

          <grid-table
            :rows="memberNominations(member)"
            :columns="COLUMNS"
            :hide-header="true"
            :no-borders="true"
            :no-stripes="true"
            border-color="white"
            border-width="2px"
            row-bg="var(--gray-200)"
          >
            <template #cell:check="{ row }">
              <div class="custom-control custom-checkbox">
                <input
                  :id="'partial-nom-' + row.id"
                  type="checkbox"
                  class="custom-control-input"
                  :checked="selectedIds.indexOf(row.id) !== -1"
                  @change="toggleSelect(row.id)"
                />
                <label class="custom-control-label mb-0" :for="'partial-nom-' + row.id" />
              </div>
            </template>

            <template #cell:ntbp="{ row }">
              <i
                v-if="row.needs_to_be_processed"
                class="nomination_need_to_be_processed"
                title="The user has unprocessed nominations after the draft rules has changed."
              />
            </template>

            <template #cell:event="{ row }">
              <div>
                <small class="d-block font-weight-bold">Event name</small>
                {{ row.event_name }}
              </div>
            </template>

            <template #cell:horse="{ row }">
              <div class="d-flex flex-column align-items-start" style="gap: 2px">
                <small class="d-block font-weight-bold">Horse</small>
                <div class="d-flex align-items-center" style="gap: 6px">
                  <i class="nom-horse-icon" :class="{ 'nom-horse-icon--crossed': row.horse.id == null }" />
                  <span class="partial-payment-popup__horse-name">{{ row.horse.name }}</span>
                </div>
                <small v-if="row.comb_nomination_id != null" class="text-primary">(Combined)</small>
              </div>
            </template>

            <template #cell:owner="{ row }">
              <div v-if="row.horse.id == null" class="font-weight-bold">
                <small class="d-block font-weight-bold">Quantity</small>
                {{ row.quantity }}
              </div>
              <div v-else class="font-weight-bold">
                <small class="d-block font-weight-bold">Horse owner</small>
                {{ row.horse.owner_name != null ? row.horse.owner_name : '—' }}
              </div>
            </template>

            <template #cell:cost="{ row }">
              <div>
                <small class="d-block font-weight-bold">Cost</small>
                <span v-if="row.cost != null">${{ Number(row.cost).toFixed(2) }}</span>
                <span v-else>—</span>
              </div>
            </template>
          </grid-table>
        </div>

        <div class="partial-payment-popup__footer mt-3 pt-3 border-top d-flex align-items-center justify-content-between">
          <app-button
            variant="primary"
            :block="false"
            size="sm"
            :loading="saving"
            @click="onUpdate"
          >
            Update
          </app-button>
          <div class="partial-payment-popup__total">
            Total paid: <strong class="ml-1">${{ totalPaid }}</strong>
          </div>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import { defineComponent, ref, computed, watch } from '@vue/composition-api'
import Modal from '@/components/Modal'
import AppButton from '@/components/AppButton'
import GridTable from '@/components/GridTable'
import FormAlert from '@/components/FormAlert'
import useNominationAccountDetail from '@/composables/nominations/useNominationAccountDetail'
import useFormAlert from '@/composables/useFormAlert'
import nominationsApi from '@/services/api/nominations'

const COLUMNS = [
  { key: 'check', label: '', width: '36px' },
  { key: 'ntbp', label: '', width: 'auto' },
  { key: 'event', label: 'Event', width: '1fr', minWidth: '120px' },
  { key: 'horse', label: 'Horse', width: '1fr', minWidth: '120px' },
  { key: 'owner', label: 'Horse Owner', width: '1fr', minWidth: '100px' },
  { key: 'cost', label: 'Cost', width: '80px' }
]

export default defineComponent({
  name: 'CommitteePartialPaymentPopup',
  components: { Modal, AppButton, GridTable, FormAlert },
  props: {
    draftId: { type: [String, Number], required: true },
    userId: { type: Number, required: true },
    paymentType: { type: String, default: null },
    onChanged: { type: Function, default: null }
  },
  emits: ['close', 'success'],
  setup(props, { emit }) {
    const { data, loading, fetch } = useNominationAccountDetail()
    const alert = useFormAlert()
    const saving = ref(false)
    const selectedIds = ref([])

    const profileRoute = computed(() => {
      if (data.value == null) return null
      return { name: 'Member Profile', params: { slug: data.value.user.id } }
    })

    function memberNominations(member) {
      const result = []
      member.events.forEach((event) => {
        event.nominations.forEach((nom) => { result.push(nom) })
      })
      return result
    }

    const flatNominations = computed(() => {
      if (data.value == null) return []
      const result = []
      data.value.members.forEach((member) => {
        member.events.forEach((event) => {
          event.nominations.forEach((nom) => { result.push(nom) })
        })
      })
      return result
    })

    const totalPaid = computed(() => {
      const sum = flatNominations.value
        .filter(n => selectedIds.value.indexOf(n.id) !== -1)
        .reduce((acc, n) => acc + (n.cost != null ? Number(n.cost) : 0), 0)
      return sum.toFixed(2)
    })

    watch(data, () => {
      if (data.value != null) {
        selectedIds.value = flatNominations.value
          .filter(n => n.is_paid)
          .map(n => n.id)
      } else {
        selectedIds.value = []
      }
    })

    watch(
      () => [props.draftId, props.userId],
      () => fetch(props.draftId, { user_id: props.userId, list: 'accept' }),
      { immediate: true }
    )

    function toggleSelect(id) {
      const idx = selectedIds.value.indexOf(id)
      const nom = flatNominations.value.find(n => n.id === id)
      const pairId = nom != null ? nom.comb_nomination_id : null
      if (idx === -1) {
        const toAdd = pairId != null && selectedIds.value.indexOf(pairId) === -1 ? [id, pairId] : [id]
        selectedIds.value = selectedIds.value.concat(toAdd)
      } else {
        selectedIds.value = selectedIds.value.filter(x => x !== id && x !== pairId)
      }
    }

    async function onUpdate() {
      saving.value = true
      alert.clear()
      try {
        if (props.paymentType != null) {
          await nominationsApi.updateCommit(props.draftId, {
            ids: flatNominations.value.map(n => n.id),
            commit: props.paymentType
          })
        }

        const items = flatNominations.value.map(n => ({
          id: n.id,
          is_paid: selectedIds.value.indexOf(n.id) !== -1 ? 1 : 0
        }))
        const res = await nominationsApi.updatePaymentStatus(props.draftId, { items })
        if (props.onChanged != null) props.onChanged()
        emit('success', res != null && res.data != null ? res.data.message : null)
        emit('close')
      } catch (err) {
        const msg = err != null && err.response != null && err.response.data != null
          ? err.response.data.message
          : 'Failed to update payment status'
        alert.showError(msg)
      } finally {
        saving.value = false
      }
    }

    return {
      COLUMNS,
      data,
      loading,
      alert,
      saving,
      selectedIds,
      profileRoute,
      flatNominations,
      totalPaid,
      memberNominations,
      toggleSelect,
      onUpdate
    }
  }
})
</script>

<style scoped>
.partial-payment-popup {
  min-width: 560px;
}

.partial-payment-popup__member-name {
  font-size: 15px;
  padding: 4px 0;
}

.partial-payment-popup__horse-name {
  font-weight: 500;
}

.partial-payment-popup__footer {
  font-size: 1rem;
}

.partial-payment-popup__total {
  font-size: 1rem;
}
</style>



// WEBPACK FOOTER //
// src/views/Committees/Dashboard/components/CommitteePartialPaymentPopup.vue